import React, { useContext, useEffect, useState } from 'react';
import { CountryCode } from '@dayetopia/types';
import { PrismicRichText } from '@prismicio/react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { BREAKPOINT } from '@constants';
import { PageSlice, ProductCode, SliceImageAndContentDetails, SliceImageAndContentItem } from '@contracts';
import { useShowInCountries } from '@hooks/useShowInCountries';
import IconCircleTick from '@icons/circle-tick.svg';
import { getMinPrice } from '@utils/boxPrices';
import { showInCountryCondition } from '@utils/country';
import Colors from '../Colors';
import LinkButton from '../LinkButton';
import PriceDisplay from '../PriceDisplay';
import { Gradient2Style } from '../SharedStyles';
import useDesktop from '../useDesktop';
import { TEXT_STYLES, TextVariant, Typography } from './Typography';

interface Props {
  slice: PageSlice;
}

const ImageAndContent: React.FC<Props> = ({ slice }) => {
  const { t } = useTranslation();
  const { currency, country } = useContext(Context).state;
  const [startingPrice, setStartingPrice] = useState<string>('-1');
  const sliceDetails = slice.primary as SliceImageAndContentDetails;
  const sliceItems = slice.items as SliceImageAndContentItem[];
  const isMobile = !useDesktop(BREAKPOINT.mobileM);
  const isTablet = !useDesktop(BREAKPOINT.laptop) && !isMobile;
  const fullWidthBackground = sliceDetails.image_full_background;
  const centeredText = sliceDetails.centered_text;
  const reverseOder = sliceDetails.reverse_order;
  const reverseOrderMobile = sliceDetails.reverse_order_mobile;
  const background = sliceDetails.background_color ?? Colors.cream;
  const originalImageAspectRatio = sliceDetails.original_image_aspect_ratio ?? false;
  const imageMaxWidthTablet = sliceDetails.image_max_width_tablet ?? 'none';
  const isFsaHsaEligible = startingPrice !== '-1' && country === CountryCode.US && sliceDetails.fsa_hsa_eligible;
  const showPrice = useShowInCountries(sliceDetails.price_show_in_countries);
  const showInCountry = showInCountryCondition(sliceDetails.show_in_countries?.text ?? '', country);
  const minHeight = sliceDetails.minimum_height ?? '640px';
  const linkTarget = sliceDetails.button_url?.includes('http') ? '_blank' : undefined;

  useEffect(() => {
    if (!sliceDetails.product_code1) return;
    const fetchStartingPrice = async () => {
      const startingPriceData = await getMinPrice(sliceDetails?.product_code1 as ProductCode, currency, country);
      setStartingPrice(startingPriceData?.price && startingPriceData?.price > 0 ? startingPriceData.price.toFixed(2) : '-1');
    };
    fetchStartingPrice();
  }, [sliceDetails?.product_code1, currency, country]);

  if (!showInCountry) {
    return null;
  }

  return (
    <Container $background={background}>
      <Wrapper $reverseOrder={reverseOder} $fullWidthBackground={fullWidthBackground} $reverseOrderMobile={reverseOrderMobile}>
        {!fullWidthBackground && (!isMobile || sliceDetails.image_mobile.url) && (
          <Image
            src={(isMobile ? sliceDetails.image_mobile.url : sliceDetails.image.url) ?? ''}
            data-testid="main-image"
            $originalImageAspectRatio={originalImageAspectRatio}
            $imageMaxWidthTablet={imageMaxWidthTablet}
          />
        )}
        <ContentBlock $fullWidthBackground={fullWidthBackground}>
          {fullWidthBackground && (
            <ImageFullWidthBackground src={(isTablet ? sliceDetails.image_mobile.url : sliceDetails.image.url) ?? ''} data-testid="main-image" />
          )}
          <ContentWrap $centeredText={centeredText} $minHeight={minHeight}>
            <HeadBlock>
              {sliceDetails.aux_image.url && <AuxImage src={sliceDetails.aux_image.url} data-testid="aux-image" />}
              {sliceDetails.overline && (
                <Typography $variant={TextVariant.UpperText4} as="h3">
                  {sliceDetails.overline}
                </Typography>
              )}
              {sliceDetails.heading.richText.length > 0 && (
                <Heading>
                  <PrismicRichText field={sliceDetails.heading.richText} />
                </Heading>
              )}
            </HeadBlock>
            {sliceDetails.subheading.richText.length > 0 && (
              <Subheading>
                <PrismicRichText field={sliceDetails.subheading.richText} />
              </Subheading>
            )}
            {sliceItems && sliceItems.length > 0 && (
              <USPRow>
                {sliceItems.map((item: SliceImageAndContentItem, index: number) => (
                  <USPItem key={index}>
                    {item.icon.url && <img src={item.icon.url} alt={item.icon.alt ?? ''} />}
                    <Typography $variant={TextVariant.BodyBoldSM}>{item.icon_text}</Typography>
                  </USPItem>
                ))}
              </USPRow>
            )}
            {sliceDetails.product_code1 && startingPrice !== '-1' && showPrice && (
              <OnlyFor>
                {t('common.only-for')}: {<PriceDisplay price={startingPrice} />}
              </OnlyFor>
            )}
            {sliceDetails.button_url && (
              <LinkButton to={sliceDetails.button_url} isInline target={linkTarget}>
                {sliceDetails.button_text_key}
              </LinkButton>
            )}
            {isFsaHsaEligible && (
              <FSAHSANotice>
                <IconCircleTick />
                {t('components.fsa-hsa-notice')}
              </FSAHSANotice>
            )}
            {sliceDetails.notice && <StyledTypography $variant={TextVariant.BodyRegularMD}>{sliceDetails.notice}</StyledTypography>}
          </ContentWrap>
          {fullWidthBackground && <ImageSpacer data-testid="spacer-image" />}
        </ContentBlock>
      </Wrapper>
    </Container>
  );
};

export default ImageAndContent;

const Container = styled.div<{ $background: string }>`
  width: 100%;
  background: ${(props) => props.$background};
`;

const Wrapper = styled.div<{ $reverseOrder: boolean; $fullWidthBackground: boolean; $reverseOrderMobile: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: ${(props) => (props.$reverseOrder ? 'row-reverse' : 'row')};
  column-gap: 64px;
  align-items: center;
  padding: 40px 80px;
  max-width: ${(props) => (props.$fullWidthBackground ? 'auto' : '1400px')};
  margin: ${(props) => (props.$fullWidthBackground ? '0' : '0 auto')};

  @media (max-width: ${BREAKPOINT.laptopM}px) {
    column-gap: 0;
  }

  @media (max-width: ${BREAKPOINT.laptop}px) {
    flex-direction: ${(props) => (props.$reverseOrderMobile ? 'column' : 'column-reverse')};
    padding: 56px 64px;
    row-gap: 40px;
    column-gap: 0;
    align-items: flex-start;
  }

  @media (max-width: ${BREAKPOINT.mobileL}px) {
    padding: 32px 24px;
    row-gap: 24px;
  }

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    padding: 32px 16px;
  }
`;

const Image = styled.img<{ $imageMaxWidthTablet: string; $originalImageAspectRatio: boolean }>`
  max-width: 50%;
  height: auto;
  aspect-ratio: ${(props) => (props.$originalImageAspectRatio ? 'auto' : '1 / 1')};
  object-fit: cover;
  border-radius: 12px;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    width: 100%;
    max-width: ${(props) => props.$imageMaxWidthTablet};
    object-fit: cover;
    aspect-ratio: ${(props) => (props.$originalImageAspectRatio ? 'auto' : '640 / 420')};
  }

  @media (max-width: ${BREAKPOINT.mobileL}px) {
    aspect-ratio: ${(props) => (props.$originalImageAspectRatio ? 'auto' : '1 / 1')};
  }
`;

const ImageSpacer = styled.div`
  width: 50%;
  height: 100%;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    width: 100%;
    height: auto;
    aspect-ratio: 640 / 420;
  }
  @media (max-width: ${BREAKPOINT.mobileL}px) {
    aspect-ratio: 1 / 1;
  }
`;

const ImageFullWidthBackground = styled.img`
  position: absolute;
  inset: 0;
  z-index: -1;
  border-radius: 12px;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeadBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: inherit;
  row-gap: 8px;

  @media (max-width: ${BREAKPOINT.mobileL}px) {
    row-gap: 16px;
  }
`;

const ContentBlock = styled.div<{ $fullWidthBackground: boolean }>`
  display: flex;
  flex-direction: row-reverse;
  position: relative;
  flex-grow: 1;
  padding: 48px;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    padding: ${(props) => (props.$fullWidthBackground ? '48px' : '0px')};
    flex-direction: column;
  }

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    padding: ${(props) => (props.$fullWidthBackground ? '16px' : '0px')};
  }
`;

interface ContentWrapProps {
  $centeredText: boolean;
  $minHeight: string;
}

const ContentWrap = styled.div<ContentWrapProps>`
  display: flex;
  flex-direction: column;
  align-items: ${(props) => (props.$centeredText ? 'center' : 'flex-start')};
  justify-content: center;
  row-gap: 32px;
  text-align: ${(props) => (props.$centeredText ? 'center' : 'left')};
  color: ${Colors.navyBlue};
  flex-grow: 1;
  padding: 0;
  min-height: ${(props) => props.$minHeight};
  position: relative;

  @media (max-width: ${BREAKPOINT.laptop}px) {
    row-gap: 16px;
    justify-content: flex-start;
    min-height: auto;
  }
`;

const AuxImage = styled.img`
  height: 40px;
  aspect-ratio: 1/1;
  margin-bottom: 8px;
`;

const Heading = styled.div`
  & > * {
    ${TEXT_STYLES[TextVariant.Heading4]};
    color: inherit;

    @media (max-width: ${BREAKPOINT.mobileM}px) {
      ${TEXT_STYLES[TextVariant.Heading6]};
    }

    & > span.italic {
      font-style: italic;
    }

    & span.gradient2 {
      ${Gradient2Style}
    }
  }
`;

const Subheading = styled.div`
  & > * {
    ${TEXT_STYLES[TextVariant.BodyRegularMD]};
    color: inherit;

    & > span.bold {
      font-weight: 700;
    }

    @media (max-width: ${BREAKPOINT.mobileM}px) {
      ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    }
  }
`;

const USPRow = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 32px;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  max-width: 650px;

  @media (max-width: ${BREAKPOINT.mobileM}px) {
    column-gap: 8px;
  }
`;

const USPItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  column-gap: 8px;

  img {
    height: 24px;
    width: auto;
  }

  p {
    ${TEXT_STYLES[TextVariant.BodyBoldSM]}
    color: inherit;
  }
`;

const StyledTypography = styled(Typography)`
  margin-top: 8px;
  opacity: 0.5;
  max-width: 650px;
`;

const FSAHSANotice = styled.div`
  ${TEXT_STYLES[TextVariant.BodyRegularMD]}
  color: inherit;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  > svg {
    height: 16px;
    width: 16px;
    margin-top: -3px;
  }
`;

const OnlyFor = styled.div`
  ${TEXT_STYLES[TextVariant.BodyRegularLG]}
  color: inherit;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  > p {
    font-size: 24px;
    color: inherit;
    margin-top: -2px;
  }
`;
